import React from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/core'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { AppStackParamList } from '@src/navigation/types'
import { isToday } from '@src/utils/global'
import { calendarDateSelector } from '@src/selectors/app'
import { Text } from '@components/base'
import { SHORT_TWELVE_HOURS_TIME_FORMAT } from '@src/config/momentFormat'
import { useRangeRepresentations } from '@src/screens/Events/utils/useRangeRepresentation'
import { usePrimaryStatsData } from '@src/screens/Home/hooks/usePrimaryStatsData'
import { User } from '@src/utils'
import { MobileAppFeature } from '@src/types'
import { HighlightsCard } from '../Highlights/HighlightsCard'

export const GlucoseSummaryCard = () => {
  const styles = useStyleSheet(themedStyles)

  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()

  const { endDate } = useSelector(calendarDateSelector)
  const isAvailable = User.hasFeature(MobileAppFeature.GlucoseLastReadingList)

  const displayLastReading = isToday(endDate)

  const {
    primaryStatRanges,
    primaryStatValue,
    primaryStatDisplayValue,
    primaryStatUnits,
    primaryStatTitle,
    lastReadingTime,
  } = usePrimaryStatsData({ displayLastReading })

  const { rangeTitle, rangeColor } = useRangeRepresentations(primaryStatRanges, primaryStatValue)
  const rangeStyle = !!rangeColor && { color: rangeColor }
  const rangeStyleSize = !!rangeStyle && rangeTitle === 'Needs Improvement' && { fontSize: 12 }

  const handleMorePress = () => {
    navigation.navigate('Scans')
  }

  if (!isAvailable) {
    return null
  }

  const title = primaryStatTitle
  const titleContentRight = displayLastReading
    ? lastReadingTime?.format(SHORT_TWELVE_HOURS_TIME_FORMAT)
    : undefined

  return (
    <HighlightsCard
      onMorePress={handleMorePress}
      title={title}
      titleContentRight={titleContentRight}
    >
      <View style={styles.statsContainer}>
        <Text type="score" style={styles.primaryStatValue}>
          {primaryStatDisplayValue}
        </Text>
        <View>
          <Text type="title-3" style={[styles.primaryStatScore, rangeStyle, rangeStyleSize]}>
            {rangeTitle ?? ''}
          </Text>
          <Text type="regular" bold style={styles.units} lineSpacing="none">
            {primaryStatUnits}
          </Text>
        </View>
      </View>
    </HighlightsCard>
  )
}

const themedStyles = StyleService.create({
  statsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  primaryStatValue: {
    justifyContent: 'flex-start',
    marginRight: 8,
  },
  primaryStatScore: {
    textTransform: 'uppercase',
  },
  units: {
    color: 'theme.text.tertiary',
    marginTop: -4,
  },
})
